import React, { useContext, useState, useEffect } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiAdjust, mdiCrosshairsGps, mdiSelectionDrag, mdiDownload } from "@mdi/js";
import commonUtils from "../../../../../../script/utils/commonUtils";

import worstcaseRest from "../../../../../../rest/worstcaseRest";
import calcAreaUtils from "../../../../../../script/utils/calcAreaUtils";
import fieldArrayResultUtils from "../../../../../../script/utils/fieldArrayResultUtils";
import plotUtils from "../../../../../../script/utils/plotUtils";
import calculationRest from "../../../../../../rest/calculationRest";

import { Legend } from "../../Legend";
import { ResultViewType } from "../ResultViewType";
import { WorstcaseDetail } from "./WorstcaseDetail";
import { Plot } from "../../../../../../script/plot";
import { Grid } from "../../../../../components/Grid";
import { Spacer } from "../../../../../components/Spacer";
import { Dialog } from "../../../../../containers/Dialog";
import { Button } from "../../../../../components/Button";
import { DialogBody } from "../../../../../containers/DialogBody";
import { EntryContext } from "../../../../../contexts/EntryContext";
import { useSaveEntry } from "../../../useSaveEntry";

const ContentStyle = styled.div`
    gap: 20px;
    display: flex;
    overflow: hidden;
    height: calc(100vh - 400px);
`;

const LeftStyle = styled.div`
    display: flex;
    width: 100%;
    overflow: hidden;
    flex-direction: column;
    .legend {
        position: relative;
        > div {
            font-size: 11px!important;
            margin: -12px 15px 1px 15px!important;
        }
    }
`;

const CanvasStyle = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    border-radius: 2px;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
`;

const RightStyle = styled.div`
    display: flex;
    width: 500px;
    flex-direction: column;
`;

const MovementButtonStyle = styled.div`
    position: relative;
    width: 100%;
    > button {
        width: 100%;
    }
`;

const ActiveDotStyle = styled.div`
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: ${props => (props.$active ? props.theme.greenLight : props.theme.black)};
    border-radius: 50%;
    bottom: 4px;
    right: 4px;
    z-index: 10;
    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
`;

export const WorstcaseResultDialog = props => {
    const { entry, calculation } = props;
    const { calcSetting } = entry;

    const entryContext = useContext(EntryContext);

    const layerName = "worstcase-side-view";
    const pointLayerName = "worstcase-side-view-point";
    const canvasId = layerName + "-canvas-" + calculation.id;

    const [plot, setPlot] = useState();
    const [detail, setDetail] = useState();

    const [legendData, setLegendData] = useState();
    const [pointSelectionActive, setPointSelectionActive] = useState(false);
    const [selection, setSelection] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(false);

    const {saving, saveEntry} = useSaveEntry();

    const dialogTitle = (() => {
        const { calcArea } = calculation;
        let title = "";
        if (calcArea.vaxisX !== undefined && calcArea.vaxisY !== undefined && calcArea.vaxisZ !== undefined) {
            const inclination = calcAreaUtils.calculateInclination(calcArea);
            if (inclination === 0) {
                title = intl.get("entry.plan.worstcase.resultDialog.topView");
            } else if (inclination === 90) {
                title = intl.get("entry.plan.worstcase.resultDialog.sideView");
            } else {
                title = intl.get("entry.plan.worstcase.resultDialog.angleView", { inclination });
            }
        }
        return title;
    })();

    const onResultClicked = opt => {
        const layer = plot.getLayer(pointLayerName);
        layer.removeObjects();
        plot.addCircle(layer, opt.scenePoint.x, opt.scenePoint.y, true);
        const {ratioU, ratioV} = plotUtils.calculteAreaSidesClickRatio(opt);
        setSelectedPoint({area: calculation.calcArea, ratioU, ratioV});
    };

    const startPointAnalysis = async () => {
        const currentEntry = entryContext.getEntry();
        await saveEntry(currentEntry);
        const {area, ratioU, ratioV} = selectedPoint;
        const slice = calcSetting.imageSetting.slice !== undefined ? calcSetting.imageSetting.slice : 0;
        const newEntry = await calculationRest.worstcasePoint(currentEntry.id, area.id, ratioU, ratioV, slice);
        entryContext.setEntry(newEntry);
        props.onClose();
    };

    useEffect(() => {
        const p = new Plot(canvasId, entry.id);
        setPlot(p);
        p.addLayer(pointLayerName, 180);
        p.addLayer(layerName, 140);
    }, []);

    useEffect(() => {
        if (plot) {
            const slice = calcSetting.imageSetting.slice !== undefined ? calcSetting.imageSetting.slice : 0;
            loadSlice(slice);
            loadColorBar(calculation.id, slice);
            loadDetail(slice);
        }
    }, [plot]);

    useEffect(() => {
        const layer = plot?.getLayer(layerName);
        if (layer) {
            if(pointSelectionActive) {
                const mouseHandler = opt => {
                    if (opt?.target?.type === "image" && pointSelectionActive) {
                        onResultClicked(opt);
                    }
                };
                layer.setHandler("mouse:down", mouseHandler);
            } else {
                const feature = plot.applyRectFeature(layer, "rgba(0,0,255,0.3)", false);
                feature.onSelect(rect => setSelection(rect));
                layer.activate();
            }
        }
        return () => layer?.removeHandler("mouse:down");
    }, [plot, pointSelectionActive]);

    const loadColorBar = async (calculationId, slice) => {
        plot.removeColorBar();
        if (calculationId) {
            const colorBarData = await worstcaseRest.getColorBar(calculationId, slice);
            plot.removeColorBar();
            plot.addColorBar(colorBarData);
        }
    };

    const loadDetail = async slice => {
        const detail = await worstcaseRest.getDetailData(calculation.id, slice);
        setDetail(detail);
    };

    const loadSlice = async slice => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();
        const result = await worstcaseRest.getResult(calculation.id, ResultViewType.SIDE, slice);
        await drawImage(layer, calculation, result);
    };

    const drawImage = async (layer, calculation, resultData) => {
        const slice = calcSetting.imageSetting.slice !== undefined ? calcSetting.imageSetting.slice : 0;
        const pointAnalysisResult = calculation.calcArea.calculations.find(c => c.worstcasePointResult)?.worstcasePointResult;
        await fieldArrayResultUtils.drawOrthogonalViewResult(
            plot, 
            setLegendData, 
            layer, 
            calculation, 
            calculation.calcArea,
            { slice },
            resultData,
            calculation.worstcaseResult,
            slice,
            pointAnalysisResult,
            calculation.calcArea.worstcaseCalcPoint,
        );
    };

    const onDownloadCanvas = () => {
        const jpegUrl = plot.toPngDataUrl();
        commonUtils.download(jpegUrl, entry.name + ".png");
        setSelection(undefined);
    };

    const onDownloadSelection = () => {
        const jpegUrl = plot.toPngDataUrl(selection);
        commonUtils.download(jpegUrl, entry.name + ".png");
        setSelection(undefined);
    };
    
    const onChange = imageSetting => {
        const slice = imageSetting.slice !== undefined ? imageSetting.slice : 0;
        loadSlice(slice);
        loadColorBar(calculation.id, slice);
        loadDetail(slice);
    };

    return (
        <Dialog
            width="1100px"
            title={dialogTitle}
            onClose={() => props.onClose()}>
            <DialogBody>
                <ContentStyle>
                    <LeftStyle>
                        <CanvasStyle id="canvas-style">
                            <canvas id={canvasId} />
                        </CanvasStyle>
                        <div className="legend">
                            <Legend
                                legendData={legendData}
                                calcSetting={calcSetting}
                                viewType={ResultViewType.SIDE}
                                plot={plot} />
                        </div>
                    </LeftStyle>
                    <RightStyle>
                        <Grid columns="2">
                            <MovementButtonStyle>
                                <Button
                                    value={intl.get("entry.plan.worstcase.pointSelectionActiate")}
                                    svgIcon={mdiCrosshairsGps}
                                    onClick={() => setPointSelectionActive(true)} />
                                <ActiveDotStyle $active={pointSelectionActive}/>
                            </MovementButtonStyle>
                            <MovementButtonStyle>
                                <Button
                                    value={intl.get("entry.plan.worstcase.selectionActiate")}
                                    svgIcon={mdiSelectionDrag}
                                    onClick={() => setPointSelectionActive(false)} />
                                <ActiveDotStyle $active={!pointSelectionActive}/>
                            </MovementButtonStyle>
                        </Grid>
                        <Spacer size="small" variant={"horizontal"}/>
                        <Grid columns="1">
                            <Button
                                simulateLoading
                                disabled={!selectedPoint || saving}
                                svgIcon={mdiAdjust}
                                onClick={() => startPointAnalysis()}
                                value={intl.get("entry.plan.worstcase.analysePoint")} />
                            <Button
                                simulateLoading
                                svgIcon={mdiDownload}
                                onClick={() => onDownloadCanvas()}
                                value={intl.get("entry.plan.download.downloadCanvas")} />
                            <Button
                                simulateLoading
                                svgIcon={mdiDownload}
                                disabled={!selection}
                                onClick={() => onDownloadSelection()}
                                value={intl.get("entry.plan.download.downloadSelection")} />
                        </Grid>
                        <Spacer variant="horizontal" />
                        {detail && detail.length > 0 &&
                            <WorstcaseDetail
                                onChange={onChange}
                                detail={detail}
                                areaName={calculation.calcArea.name}
                                calcSetting={calcSetting}
                                disabled={false} />
                        }
                    </RightStyle>
                </ContentStyle>
            </DialogBody>
        </Dialog>
    );
};
